import { isPrimitiveOrPrimitiveClass, isArrayOrArrayClass } from './is-utils.js';

/**
 * 获取 Provider 的构造函数
 * @param targetClass - Provider 的 Class 定义
 * @public
 */
const getContructor = (targetClass) => (typeof targetClass === 'function'
    ? targetClass
    : targetClass.constructor);
/**
 * 获取 Provider 的构造函数（基于实例）
 *
 * @param target - Provider 的实例
 * @public
 */
function getClass(target) {
    return (target.prototype ? target : target.constructor);
}
/**
 * 获取 Provider 构造函数或 Symbol
 *
 * @param target - Provider 的实例
 * @public
 */
function getClassOrSymbol(target) {
    return typeof target === 'symbol' ? target : getClass(target);
}
/**
 * 判断是否是空字符串或 null 或未定义
 * @param value - 待判断值
 * @public
 */
function isEmpty(value) {
    return value === '' || value === null || value === undefined;
}
/**
 * 获取 Class 的名称
 *
 * @param targetClass - 目标类
 * @public
 */
const nameOfClass = (targetClass) => {
    var _a;
    return (typeof targetClass === 'function'
        ? (_a = targetClass.displayName) !== null && _a !== void 0 ? _a : targetClass.name
        : nameOfClass(targetClass.constructor));
};
/**
 * 获取 Symbol 的名称
 *
 * @param sym - 目标 symbol
 * @public
 */
const nameOfSymbol = (sym) => sym.toString().replace('Symbol(', '')
    .replace(')', '');
/**
 * 获取对象名
 * @public
 */
const nameOf = (obj) => {
    switch (typeof obj) {
        default:
            return `${obj}`;
        case 'symbol':
            return nameOfSymbol(obj);
        case 'function':
            return nameOfClass(obj);
    }
};
/**
 * 获取父类
 * @public
 */
function getInheritedClass(target) {
    return Object.getPrototypeOf(target);
}
/**
 * 获取子类
 * @public
 */
function ancestorsOf(target) {
    const classes = [];
    let currentTarget = getClass(target);
    while (nameOf(currentTarget) !== '') {
        classes.unshift(currentTarget);
        currentTarget = getInheritedClass(currentTarget);
    }
    return classes;
}
/**
 * 返回指定对象上一个自有属性对应的属性描述符。
 * （自有属性指的是直接赋予该对象的属性，不需要从原型链上进行查找的属性）
 *
 * @param target - 对象
 * @param propertyKey - 属性名
 * @public
 */
function descriptorOf(target, propertyKey) {
    var _a;
    return Object.getOwnPropertyDescriptor((_a = (target === null || target === void 0 ? void 0 : target.prototype)) !== null && _a !== void 0 ? _a : target, propertyKey);
}
/**
 * 继承一个值，递归的继承子属性
 * @public
 */
function deepExtends(out, obj, reducers = {}) {
    if (obj === undefined || obj === null) {
        return obj;
    }
    if (isPrimitiveOrPrimitiveClass(obj) || typeof obj === 'symbol' || typeof obj === 'function') {
        return obj;
    }
    if (isArrayOrArrayClass(obj)) {
        out = out || [];
    }
    else {
        out = out || {};
    }
    const defaultReducer = reducers.default
        ? reducers.default
        : (collection, value) => {
            collection.push(value);
            return collection;
        };
    const set = (key, value) => {
        if (isArrayOrArrayClass(obj)) {
            out.push(value);
        }
        else {
            out[key] = value;
        }
    };
    Object.keys(obj).forEach((key) => {
        let value = obj[key];
        if (value === undefined || value === null) {
            return;
        }
        if (value === '' && out[key] !== '') {
            return;
        }
        if (isPrimitiveOrPrimitiveClass(value) || typeof value === 'function') {
            set(key, value);
            return;
        }
        if (isArrayOrArrayClass(value)) {
            value = value.map((v) => deepExtends(undefined, v));
            set(key, []
                .concat(out[key] || [], value)
                .reduce((collection, v) => (reducers[key]
                ? reducers[key](collection, v)
                : defaultReducer(collection, value)), []));
            return;
        }
        // Object
        if (isArrayOrArrayClass(obj)) {
            set(key, deepExtends(undefined, value, reducers));
        }
        else {
            set(key, deepExtends(out[key], value, reducers));
        }
    });
    if (isArrayOrArrayClass(out)) {
        out.reduce((collection, value) => defaultReducer(collection, value), []);
    }
    return out;
}
function isNull(data) {
    return data === null;
}
function isUndefined(data) {
    return data === undefined;
}
/**
 * data 是否为需要过滤数据
 * 过滤内容包含： undefined null
 */
function isFilterData(data) {
    return isNull(data) || isUndefined(data);
}
function isObject(data) {
    // 当前非 null 的 object
    return typeof data === 'object' && !isNull(data);
}
/**
 * 深度比较传入的两个参数是否相同
 * 1、若有参数为 undefined null 则认为不相等
 * 2、可以比较类型有: map object array number symbol string boolean
 *
 * @public
 */
/* eslint complexity: ["error", 20] */
function isEqual(dataOne, dataTwo) {
    try {
        // 1、当前两个参数相等，栈为同一个 或 值相等，排除 undefined、null 情况
        if (dataOne === dataTwo) {
            return !(isFilterData(dataOne));
        }
        // 2、处理 非 object array map
        if (!isObject(dataOne) || !isObject(dataTwo)) {
            return false;
        }
        // 3、处理 Array
        if ((Array.isArray(dataOne)) || (Array.isArray(dataTwo))) {
            // 只要有一个为 array 就强制进入此逻辑，否则会走到下面处理object逻辑，则出现isEqual([1, 2, 3],{ 0: 1, 1: 2, 2: 3 })为true的错误情况
            if (!(Array.isArray(dataOne)) || !(Array.isArray(dataTwo)))
                return false;
            if (dataOne.length !== dataTwo.length)
                return false;
            return !(dataOne.some((value, index) => !isEqual(value, dataTwo[index])));
        }
        // 4、处理 map
        if ((dataOne instanceof Map) || (dataTwo instanceof Map)) {
            if (!(dataOne instanceof Map) || !(dataTwo instanceof Map))
                return false;
            if (dataOne.size !== dataTwo.size)
                return false;
            const dataOneItem = dataOne.entries();
            let itemValue = dataOneItem.next().value;
            while (itemValue) {
                const [key, value] = itemValue;
                if (!isEqual(value, dataTwo.get(key)))
                    return false;
                itemValue = dataOneItem.next().value;
            }
            return true;
        }
        // 5、处理 object
        if ((dataOne instanceof Object) && (dataTwo instanceof Object)) {
            if (Object.keys(dataOne).length !== Object.keys(dataTwo).length)
                return false;
            return !(Object.entries(dataOne).some(([key, value]) => !isEqual(value, dataTwo[key])));
        }
    }
    catch (error) {
        console.error('workbench deep equal function crash', error);
        return false;
    }
    return false;
}

export { ancestorsOf, deepExtends, descriptorOf, getClass, getClassOrSymbol, getContructor, getInheritedClass, isEmpty, isEqual, nameOf, nameOfClass, nameOfSymbol };
//# sourceMappingURL=object-utils.js.map
